import { useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Route external image URLs through our /api/img proxy so users on
 * networks that block third-party CDNs (Unsplash, etc.) still see images.
 * Local / data / already-proxied URLs are returned unchanged.
 */
const proxied = (src) => {
  if (!src) return src;
  if (src.startsWith("data:")) return src;
  // Uploaded files: prefix with backend origin, no proxy (already same-domain in prod)
  if (src.startsWith("/api/")) return `${BACKEND_URL}${src}`;
  if (src.startsWith("/")) return src;
  try {
    const u = new URL(src);
    if (
      u.host === "images.unsplash.com" ||
      u.host === "customer-assets.emergentagent.com" ||
      u.host === "shantanunetralaya.com"
    ) {
      return `${BACKEND_URL}/api/img?url=${encodeURIComponent(src)}`;
    }
  } catch {
    // fall through
  }
  return src;
};

/**
 * Resilient image with a branded gradient fallback + optional label.
 * Automatically routes external images through the backend proxy.
 */
const SafeImage = ({ src, alt = "", className = "", loading = "lazy", label, style, width, height, ...rest }) => {
  const [failed, setFailed] = useState(!src);
  const initial = (label || alt || "•").trim().charAt(0).toUpperCase() || "•";

  return (
    <div
      style={style}
      className={`relative overflow-hidden bg-gradient-to-br from-[hsl(var(--primary))]/12 via-[hsl(var(--secondary))] to-[hsl(var(--accent))]/15 ${className}`}
      role={alt ? undefined : "presentation"}
    >
      {!failed && src && (
        <img
          src={proxied(src)}
          alt={alt}
          loading={loading}
          decoding="async"
          fetchPriority={loading === "eager" ? "high" : "auto"}
          width={width || 1200}
          height={height || 900}
          onError={() => setFailed(true)}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700"
          {...rest}
        />
      )}
      {failed && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
          <div className="w-14 h-14 rounded-full bg-[hsl(var(--primary))]/15 border border-[hsl(var(--primary))]/25 flex items-center justify-center">
            <span className="font-serif text-2xl text-[hsl(var(--primary))]">{initial}</span>
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">
            Shantanu Netralaya
          </div>
        </div>
      )}
    </div>
  );
};

export default SafeImage;
